import { useState, useCallback, useEffect, useMemo } from 'react';
import { getFaceCandidates } from '../api';
import { FACES_PER_PAGE } from '../utils/constants';

/**
 * Custom hook for loading person suggestions (candidates) for the selected
 * face.
 *
 * Uses GET /api/faces/{id}/candidates/ — the server mirrors the auto-match
 * pgvector query, dedupes to one row per person, applies the anchor-pool
 * filters (labelled, not soft-deleted, not ignored) and returns rows sorted
 * by distance with `confidence` from the shared auto-match curve.
 *
 * The old client-side >=80% raw-similarity filter is gone on purpose: the
 * near-threshold band (the candidates auto-match rejected at d=0.40) is
 * exactly what an editor labeling an orange face wants to see.
 *
 * @param {Object} options - Hook options
 * @param {Object} options.selectedFace - Current selected face (needs .id)
 * @returns {Object} Neighbor search state and handlers
 */
export default function useNeighborSearch({ selectedFace }) {
  // Candidate state (kept under the historical "neighbors" names so the
  // Edit page's destructuring stays stable)
  const [neighbors, setNeighbors] = useState([]);
  const [loadingNeighbors, setLoadingNeighbors] = useState(false);
  const [neighborsPage, setNeighborsPage] = useState(1);
  const [threshold, setThreshold] = useState(0.35);

  /**
   * Load person candidates for a face
   */
  const loadNeighbors = useCallback(async (face) => {
    if (!face || !face.id) {
      setNeighbors([]);
      return;
    }
    setLoadingNeighbors(true);
    try {
      const res = await getFaceCandidates(face.id, 8);
      setNeighbors(res);
    } catch (e) {
      console.error(e);
      setNeighbors([]);
    } finally {
      setLoadingNeighbors(false);
    }
  }, []);

  // Load candidates when selectedFace changes
  useEffect(() => {
    loadNeighbors(selectedFace);
  }, [selectedFace, loadNeighbors]);

  // Reset page when candidates change
  useEffect(() => {
    setNeighborsPage(1);
  }, [neighbors]);

  // Server already filters, dedupes per person and sorts by distance — no
  // client-side confidence cutoff anymore (near-threshold rows must show).
  const highConfNeighbors = neighbors;

  // Pagination calculations
  const totalNeighborPages = useMemo(() => {
    return Math.max(1, Math.ceil((highConfNeighbors.length || 0) / FACES_PER_PAGE));
  }, [highConfNeighbors.length]);

  const currentNeighborPage = useMemo(() => {
    return Math.min(neighborsPage, totalNeighborPages);
  }, [neighborsPage, totalNeighborPages]);

  const pageNeighbors = useMemo(() => {
    return highConfNeighbors.slice(
      (currentNeighborPage - 1) * FACES_PER_PAGE,
      currentNeighborPage * FACES_PER_PAGE
    );
  }, [highConfNeighbors, currentNeighborPage]);

  /**
   * Go to previous page of neighbors
   */
  const prevNeighborsPage = useCallback(() => {
    setNeighborsPage((p) => Math.max(1, p - 1));
  }, []);

  /**
   * Go to next page of neighbors
   */
  const nextNeighborsPage = useCallback(() => {
    setNeighborsPage((p) => Math.min(totalNeighborPages, p + 1));
  }, [totalNeighborPages]);

  return {
    // Raw state
    neighbors,
    loadingNeighbors,
    neighborsPage,
    setNeighborsPage,
    threshold,
    setThreshold,

    // Derived state
    highConfNeighbors,
    totalNeighborPages,
    currentNeighborPage,
    pageNeighbors,

    // Handlers
    loadNeighbors,
    prevNeighborsPage,
    nextNeighborsPage,
  };
}
